/**
 * Shortening for the wallet addresses that stand in for a username.
 *
 * A 42 character address does not fit any of the rows it appears in, so it is
 * shown as its first four characters and its last four - 0x12...ABCD - which is
 * the part people recognise. Some rows arrive already cut short by the API, with
 * a trailing ellipsis; that is dropped first so those end up in the same shape as
 * the rest rather than as a ragged stump.
 */

/** Marks a value as a wallet address rather than a chosen username */
const WALLET_PREFIX = /^0x/i;
/** A truncation the API has already applied, e.g. 0x517caf70F3752... */
const TRAILING_ELLIPSIS = /[.…]+$/;
/** Anything at or under this length is already short enough to show whole */
const SHORTEN_ABOVE = 12;

/** Hex characters kept after the 0x when no other count is asked for */
const DEFAULT_HEAD_HEX = 2;

/**
 * @param value - The name or wallet address to display
 * @param headHexChars - Hex characters to keep after the 0x. Rows that have the
 *        width for it ask for more, e.g. 4 gives 0x0f51...2ceb
 * @returns The address in its shortened form, or the value unchanged when it is
 *          not an address
 */
export function shortenWalletAddress(value: string, headHexChars: number = DEFAULT_HEAD_HEX): string {
  const name = (value ?? '').trim();

  if (!WALLET_PREFIX.test(name)) {
    return name;
  }

  const address = name.replace(TRAILING_ELLIPSIS, '');

  const head = address.slice(0, '0x'.length + headHexChars);

  return address.length > SHORTEN_ABOVE
    ? `${head}...${address.slice(-4)}`
    : address;
}
